import { Component, Host, h, Element, Prop } from "@stencil/core";
import { ObservableMap } from "@stencil/store";
import dayjs from "dayjs";
import { Datepicker } from "vanillajs-datepicker";
import nl from "vanillajs-datepicker/js/i18n/locales/nl";
import { getStore, ILiftStatusState } from "../../store";
Object.assign(Datepicker.locales, nl);

interface ITimeOptions {
  value: string[];
  label: string;
}

@Component({
  tag: "ls-date-picker",
  styleUrl: "ls-date-picker.css",
  shadow: true,
})
export class DatePicker {
  /**
   * The Host element
   */
  @Element() element: HTMLLsDatePickerElement;

  /**
   * Time options used to populate the select element
   */
  private timeOptions: ITimeOptions[] = [];

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  private store: ObservableMap<ILiftStatusState>;

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
    this.timeOptions = this.getTimeOptions();
  }

  componentDidLoad() {
    // Select the date picker element
    const datePickerElement =
      this.element.shadowRoot.querySelector<HTMLElement>("#date-picker");

    // Initialize the date picker
    const datePicker = new Datepicker(datePickerElement, {
      language: "nl",
    });

    // Set today as the selected date
    datePicker.setDate(dayjs(this.store.state.datePicker.start).valueOf());

    // Subscribe to date picker element changes
    datePickerElement.addEventListener("changeDate", (_event: CustomEvent) => {
      const selectedDate = dayjs(new Date(datePicker.getDate().toString())); // Only way to get the selected date consistently
      const currentStart = dayjs(this.store.state.datePicker.start);
      const currentEnd = dayjs(this.store.state.datePicker.end);

      this.store.state.datePicker = {
        ...this.store.state.datePicker,
        // Only overwrite the date, keep the time
        start: selectedDate
          .hour(currentStart.hour())
          .minute(currentStart.minute())
          .second(currentStart.second())
          .millisecond(currentStart.millisecond())
          .toISOString(),
        // Only overwrite the date, keep the time
        end: selectedDate
          .hour(currentEnd.hour())
          .minute(currentEnd.minute())
          .second(currentEnd.second())
          .millisecond(currentEnd.millisecond())
          .toISOString(),
      };
    });

    // Subscribe to store changes
    this.store.onChange("datePicker", ({ start }) => {
      datePicker.setDate(dayjs(start).valueOf());

      // Update the time options (needed for the timestamp values)
      this.timeOptions = this.getTimeOptions();
    });
  }

  private handleSelect = (event: Event) => {
    const selectElement = event.target as HTMLSelectElement;
    const selectedStart = dayjs(selectElement.value.split(",")[0]);
    const selectedEnd = dayjs(selectElement.value.split(",")[1]);
    const currentStart = dayjs(this.store.state.datePicker.start);

    this.store.state.datePicker = {
      ...this.store.state.datePicker,
      // Only overwrite the date, keep the time
      start: currentStart
        .hour(selectedStart.hour())
        .minute(selectedStart.minute())
        .second(selectedStart.second())
        .millisecond(selectedStart.millisecond())
        .toISOString(),
      // Only overwrite the date, keep the time
      end: currentStart
        .hour(selectedEnd.hour())
        .minute(selectedEnd.minute())
        .second(selectedEnd.second())
        .millisecond(selectedEnd.millisecond())
        .toISOString(),
    };
  }

  private decrementHour = () => {
    const currentStart = dayjs(this.store.state.datePicker.start);
    const currentEnd = dayjs(this.store.state.datePicker.end);

    this.store.state.datePicker = {
      ...this.store.state.datePicker,
      start: currentStart.subtract(1, "hour").toISOString(),
      end: currentEnd.subtract(1, "hour").toISOString(),
    };
  };

  private incrementHour = () => {
    const currentStart = dayjs(this.store.state.datePicker.start);
    const currentEnd = dayjs(this.store.state.datePicker.end);

    this.store.state.datePicker = {
      ...this.store.state.datePicker,
      start: currentStart.add(1, "hour").toISOString(),
      end: currentEnd.add(1, "hour").toISOString(),
    };
  };

  private incrementDay = () => {
    const currentStart = dayjs(this.store.state.datePicker.start);
    const currentEnd = dayjs(this.store.state.datePicker.end);

    this.store.state.datePicker = {
      ...this.store.state.datePicker,
      start: currentStart.add(1, "day").toISOString(),
      end: currentEnd.add(1, "day").toISOString(),
    };
  };

  private decrementDay = () => {
    const currentStart = dayjs(this.store.state.datePicker.start);
    const currentEnd = dayjs(this.store.state.datePicker.end);

    this.store.state.datePicker = {
      ...this.store.state.datePicker,
      start: currentStart.subtract(1, "day").toISOString(),
      end: currentEnd.subtract(1, "day").toISOString(),
    };
  };

  /**
   * Use to initialize select dropdown options
   * @returns
   */
  private getTimeOptions = () => {
    const timeFrameData: ITimeOptions[] = [];

    for (let i = 0; i < 24; i++) {
      const startLabel = i < 10 ? `0${i}` : i;
      const endLabel = i + 1 < 10 ? `0${i + 1}` : i + 1;

      timeFrameData.push({
        value: [
          dayjs(this.store.state.datePicker.start).hour(i).toISOString(),
          dayjs(this.store.state.datePicker.end)
            .hour(i + 1)
            .toISOString(),
        ],
        label: `${startLabel}:00 - ${endLabel}:00`,
      });
    }

    return timeFrameData;
  };

  render() {
    return (
      <Host>
        <input type="text" id="date-picker" />
        <select onInput={this.handleSelect}>
          {this.timeOptions.map(({ value, label }) => (
            <option
              value={value}
              selected={
                value[0] === this.store.state.datePicker.start &&
                value[1] === this.store.state.datePicker.end
              }
            >
              {label}
            </option>
          ))}
        </select>
        <button onClick={this.decrementHour}>&#8249;</button>
        <button onClick={this.incrementHour}>&#8250;</button>
        <button onClick={this.decrementDay}>&#171;</button>
        <button onClick={this.incrementDay}>&#187;</button>
      </Host>
    );
  }
}
